import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import {
  BarcodeIcon,
  SaveSpaceForParagraph,
  FlexBox,
  PrimaryButton,
  EmptyState,
  SpacingBox,
  DebouncedGreyInvertedBigInput,
  H2,
  InfoParagraph,
} from '../../Components';
import { translate } from './translate';
import { scannerStatuses } from './barcodeScanner';
import { WithProductsSearchWithInput } from '../WithProductsSearch';
import ProductSearchPreview from '../ProductSearchPreview';
import styles from './styles.module.scss';

export const modalStyles = {
  width: '92vw',
  maxWidth: 1280,
  position: 'relative',
  top: '40px',
  bottom: '50px',
  maxHeight: '85vh',
  overflow: 'auto',
};

const NO_DETECTION_HINT_DELAY_MS = 5000;

const navLabel = (isScanning, scannerStatus) => {
  if (!isScanning) return translate('scan');
  if (scannerStatus === scannerStatuses.error) return translate('cameraError');
  return translate('scanning');
};

const navColor = (isScanning, scannerStatus) => {
  if (!isScanning) return 'black';
  if (scannerStatus === scannerStatuses.error) return 'red';
  return 'green';
};

export const ScannerNavTrigger = ({
  isScanning,
  scannerStatus,
  onToggleHiddenScan,
  onOpenModal,
}) => (
  <>
    <SpacingBox marginRight="sm">
      <div
        style={{
          cursor: 'pointer',
          fontWeight: 'bold',
          userSelect: 'none',
          minHeight: '48px',
          lineHeight: '48px',
          color: navColor(isScanning, scannerStatus),
        }}
        onClick={onToggleHiddenScan}
      >
        {navLabel(isScanning, scannerStatus)}
      </div>
    </SpacingBox>
    <BarcodeIcon onClick={onOpenModal} />
  </>
);

export const EmptyOrderState = ({ onGoToCustomerSelection }) => (
  <FlexBox
    padding="sm"
    flex={1}
    flexDirection="column"
    alignItems="center">
    <EmptyState
      title={translate('emptyState.title')}
      description={translate('emptyState.description')}
    />
    <PrimaryButton
      onClick={onGoToCustomerSelection}
      text={translate('emptyState.button')}
    />
  </FlexBox>
);

const EanSearchInput = ({ inputValue, validation }) => (
  <div className={styles.eanColumn}>
    <p className={styles.eanLabel}>{translate('label')}</p>
    <p className={styles.eanDigits}>{inputValue}</p>
    <SaveSpaceForParagraph value={validation}>
      <p className={styles.eanError}>{validation}</p>
    </SaveSpaceForParagraph>
  </div>
);

const videoVisibleStyle = {
  border: '1px solid gray',
  width: 253,
  height: 253,
};

/** Kept in DOM (off-screen when collapsed) so ZXing still finds #video for nav "Scan" without modal. */
const videoHiddenStyle = {
  ...videoVisibleStyle,
  position: 'fixed',
  left: -9999,
  top: 0,
  opacity: 0,
  pointerEvents: 'none',
};

// Shown once the camera has been live for a while without reading a barcode;
// restarts whenever something is scanned
const useNoDetectionHint = (scannerStatus, scanCount) => {
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    setShowHint(false);
    if (scannerStatus !== scannerStatuses.scanning) return undefined;
    const id = window.setTimeout(
      () => setShowHint(true),
      NO_DETECTION_HINT_DELAY_MS,
    );
    return () => window.clearTimeout(id);
  }, [scannerStatus, scanCount]);

  return showHint;
};

const statusMessage = (scannerStatus, showNoDetectionHint) => {
  switch (scannerStatus) {
    case scannerStatuses.starting:
      return translate('status.starting');
    case scannerStatuses.scanning:
      return showNoDetectionHint
        ? translate('status.noBarcodeDetected')
        : translate('status.scanning');
    case scannerStatuses.error:
      return translate('status.error');
    default:
      return null;
  }
};

const ScannerStatus = ({ scannerStatus, scanCount }) => {
  const showNoDetectionHint = useNoDetectionHint(scannerStatus, scanCount);
  const message = statusMessage(scannerStatus, showNoDetectionHint);
  if (!message) return null;

  return (
    <p
      className={cn(styles.scannerStatus, {
        [styles.scannerStatusActive]: scannerStatus === scannerStatuses.scanning
          && !showNoDetectionHint,
        [styles.scannerStatusWarning]: showNoDetectionHint,
        [styles.scannerStatusError]: scannerStatus === scannerStatuses.error,
      })}
    >
      {message}
    </p>
  );
};

const CameraScanner = ({
  onStartCamera,
  scannerStatus,
  scanCount,
  isModalOpen,
}) => {
  const [showCameraUi, setShowCameraUi] = useState(false);
  const isCameraOn = scannerStatus === scannerStatuses.starting
    || scannerStatus === scannerStatuses.scanning;

  useEffect(() => {
    if (!isModalOpen) {
      setShowCameraUi(false);
    }
  }, [isModalOpen]);

  return (
    <div>
      <video
        id="video"
        style={showCameraUi ? videoVisibleStyle : videoHiddenStyle}
      />
      {!showCameraUi ? (
        <PrimaryButton
          onClick={() => setShowCameraUi(true)}
          text={translate('showCameraScanner')}
        />
      ) : (
        <SpacingBox marginTop="sm">
          {!isCameraOn && (
            <PrimaryButton
              onClick={onStartCamera}
              text={translate('scanUsingCamera')}
            />
          )}
          <ScannerStatus scannerStatus={scannerStatus} scanCount={scanCount} />
        </SpacingBox>
      )}
    </div>
  );
};

export const ScannerSection = ({
  inputValue,
  validation,
  onStartCamera,
  scannerStatus,
  scanCount,
  isModalOpen,
}) => (
  <>
    <FlexBox
      flex={1}
      paddingTop="md"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      marginTop="md">
      <H2>{translate('scanner')}</H2>
      <InfoParagraph className={styles.mutedLead}>
        {translate('scannerDescription')}
      </InfoParagraph>
    </FlexBox>
    <FlexBox
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      paddingTop="sm"
    >
      <div style={{ borderRight: '1px solid rgb(152,152,152)' }}>
        <FlexBox flexDirection="column" style={{ paddingRight: '3vw' }}>
          <CameraScanner
            onStartCamera={onStartCamera}
            scannerStatus={scannerStatus}
            scanCount={scanCount}
            isModalOpen={isModalOpen}
          />
        </FlexBox>
      </div>
      <FlexBox flexDirection="column" style={{ paddingLeft: '3vw' }}>
        <EanSearchInput inputValue={inputValue} validation={validation} />
      </FlexBox>
    </FlexBox>
  </>
);

export const ProductSearchSection = ({ onSelectProduct }) => (
  <>
    <FlexBox
      flex={1}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      marginTop="sm">
      <H2>{translate('searchAProduct')}</H2>
      <InfoParagraph className={styles.mutedLead}>
        {translate('searchAProductDescription')}
      </InfoParagraph>
    </FlexBox>
    <FlexBox
      flexDirection="column"
      className={cn('searchBox', styles.productSearch)}
      style={{ minHeight: 400, paddingLeft: '5vw', paddingRight: '5vw' }}>
      <WithProductsSearchWithInput
        defaultSearch=""
        page={1}
        renderInputComponent={({ search, handleSearch }) => (
          <DebouncedGreyInvertedBigInput
            placeholder="Search"
            onChange={handleSearch}
            value={search}
          />
        )}>
        {({ products, search, setSearch }) => (
          <SpacingBox>
            <ProductSearchPreview
              products={products}
              search={search}
              onClick={(number) => {
                const searchInput = document.querySelector('.searchBox input');
                searchInput.focus();
                onSelectProduct(number);
                setSearch('');
              }}
              onRouteToAllResults={null}
              totalDisplayed={60}
            />
          </SpacingBox>
        )}
      </WithProductsSearchWithInput>
    </FlexBox>
  </>
);
